import React, {
  forwardRef,
  ReactElement,
  ForwardedRef,
  RefObject,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import uniqueId from 'lodash/uniqueId';
import isNil from 'lodash/isNil';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import QuickTipIcon from 'assets/svg/quickTip';

  type Props = {
    className?: string;
    popperClassName?: string;
    title?: string | ReactNode;
    content?: string;
    children: ReactElement;
    placement?: 'top' | 'bottom' | 'right' | 'left' | 'bottom-start' | 'top-end';
    theme?: 'dark' | 'light';
    hasIcon?: boolean;
    hide?: boolean;
    auto?: boolean;
    disablePortal?: boolean;
  };

function checkIfTextOverflowing(element: HTMLElement | null) {
  if (isNil(element)) return false;

  return Number(element.offsetWidth) < Number(element.scrollWidth);
}

const resizeHandlerRegistry: Record<string, (overflown: boolean) => void> = {};
const ToolTipResizeObserver = new ResizeObserver((entries) => {
  if (!Array.isArray(entries)) return;

  window.requestAnimationFrame(() => {
    entries.forEach((entry: ResizeObserverEntry) => {
      const element = entry.target as HTMLElement;
      const uniqId = element.dataset.populateTooltipId ?? '';
      const handler = resizeHandlerRegistry[uniqId];

      if (typeof handler === 'function') {
        handler(checkIfTextOverflowing(element));
      }
    });
  });
});

const ToolTip = forwardRef(({
  className,
  popperClassName,
  title = 'Quick Tip',
  content,
  children,
  placement = 'top',
  theme = 'light',
  hasIcon = false,
  auto = false,
  hide,
  disablePortal = false,
}: Props, ref: ForwardedRef<HTMLElement>) => {
  const [overflowed, setOverflowed] = useState<boolean>(false);
  const classes = clsx('flex flex-col justify-center items-start', className);
  const popperClasses = clsx(popperClassName, { 'tooltip-theme-light': theme === 'light' });

  useEffect(() => {
    const isOverflowed = checkIfTextOverflowing((ref as RefObject<HTMLElement>)?.current);
    setOverflowed(isOverflowed);
  }, []);

  useEffect(() => {
    const element = (ref as RefObject<HTMLElement>)?.current;
    if (isNil(element) || !auto) return () => {};

    const uniqId = uniqueId('tooltip-id-');
    element.dataset.populateTooltipId = uniqId;
    resizeHandlerRegistry[uniqId] = setOverflowed;
    ToolTipResizeObserver.observe(element);

    return () => {
      delete resizeHandlerRegistry[element.dataset.populateTooltipId ?? ''];
      ToolTipResizeObserver.unobserve(element);
    };
  }, [ref, auto]);

  const isHidden = auto ? !overflowed : hide;

  return (
    <Tooltip
      classes={{ popper: popperClasses }}
      title={(
        <div className={classes}>
          <div className="flex justify-start items-center space-x-1 w-full">
            {hasIcon && <QuickTipIcon />}
            <p className="text-xs font-light w-full">{title}</p>
          </div>
          <p className="text-xs font-medium">{content}</p>
        </div>
        )}
      placement={placement}
      arrow
      disableFocusListener={isHidden}
      disableHoverListener={isHidden}
      disableTouchListener={isHidden}
      disableInteractive
      PopperProps={{ disablePortal }}
    >
      {children}
    </Tooltip>
  );
});

export default ToolTip;
